/*  QuizBuilder — Screen 4 (embedded in LessonEditor for quiz-type lessons).
    ───────────────────────────────────────────────────────────────────────
    Manages the question bank for a single quiz-lesson + global settings
    (pass score, max attempts, retake allowed).

    Question types:
      • MCQ         — 2-6 options, single correct
      • True/False  — always 2 options: True, False
      • Scenario    — 2-6 options, single correct (case-study framing)

    Saves via PUT /api/course-builder/lessons/{id}/quiz (full replacement).
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Plus, Trash, CheckCircle, Circle, Question, ListChecks, ToggleLeft,
    Sparkle, Warning, FloppyDisk,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import RichTextEditor from "@/components/creator/RichTextEditor";
import { htmlIsEmpty } from "@/components/creator/RichTextView";


const TYPE_META = {
    mcq:         { label: "Multiple Choice", Icon: ListChecks, defaultOptions: ["Option A", "Option B", "Option C", "Option D"] },
    true_false:  { label: "True / False",    Icon: ToggleLeft, defaultOptions: ["True", "False"] },
    scenario:    { label: "Scenario",        Icon: Sparkle,    defaultOptions: ["Option A", "Option B", "Option C"] },
};


export default function QuizBuilder({ lesson, readOnly, onSaved }) {
    const [quiz, setQuiz] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/course-builder/lessons/${lesson.id}/quiz`);
            setQuiz({
                questions: (data.questions || []).map((q) => ({ ...q })),
                pass_score: data.pass_score ?? 0.7,
                max_attempts: data.max_attempts ?? 3,
                retake_allowed: data.retake_allowed ?? true,
            });
            setDirty(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load quiz.");
        } finally {
            setLoading(false);
        }
    }, [lesson.id]);

    useEffect(() => { load(); }, [load]);

    const set = useCallback((patch) => {
        setQuiz((prev) => ({ ...prev, ...patch }));
        setDirty(true);
    }, []);

    const setQuestion = useCallback((idx, patch) => {
        setQuiz((prev) => {
            const next = { ...prev, questions: prev.questions.map((q, i) => (i === idx ? { ...q, ...patch } : q)) };
            return next;
        });
        setDirty(true);
    }, []);

    const addQuestion = (type) => {
        const meta = TYPE_META[type];
        setQuiz((prev) => ({
            ...prev,
            questions: [
                ...prev.questions,
                {
                    type,
                    prompt: "",
                    options: [...meta.defaultOptions],
                    answer_index: 0,
                    rationale: "",
                },
            ],
        }));
        setDirty(true);
    };

    const removeQuestion = (idx) => {
        setQuiz((prev) => ({ ...prev, questions: prev.questions.filter((_, i) => i !== idx) }));
        setDirty(true);
    };

    const addOption = (idx) => {
        setQuiz((prev) => {
            const q = prev.questions[idx];
            if (!q || q.type === "true_false" || (q.options || []).length >= 6) return prev;
            const nextOpts = [...q.options, `Option ${String.fromCharCode(65 + q.options.length)}`];
            return { ...prev, questions: prev.questions.map((row, i) => (i === idx ? { ...row, options: nextOpts } : row)) };
        });
        setDirty(true);
    };

    const removeOption = (qIdx, optIdx) => {
        setQuiz((prev) => {
            const q = prev.questions[qIdx];
            if (!q || q.type === "true_false" || (q.options || []).length <= 2) return prev;
            const nextOpts = q.options.filter((_, i) => i !== optIdx);
            let nextAns = q.answer_index;
            if (optIdx === q.answer_index) nextAns = 0;
            else if (optIdx < q.answer_index) nextAns = q.answer_index - 1;
            return { ...prev, questions: prev.questions.map((row, i) => (i === qIdx ? { ...row, options: nextOpts, answer_index: nextAns } : row)) };
        });
        setDirty(true);
    };

    const validation = useMemo(() => {
        if (!quiz) return { ok: false, errors: [] };
        const errors = [];
        if (!quiz.questions.length) errors.push("Add at least one question.");
        quiz.questions.forEach((q, i) => {
            if (htmlIsEmpty(q.prompt)) errors.push(`Q${i + 1}: prompt is empty.`);
            if (!(q.options || []).length || q.options.some((o) => !(o || "").trim())) errors.push(`Q${i + 1}: options can't be empty.`);
            if (q.answer_index >= (q.options || []).length) errors.push(`Q${i + 1}: pick a correct answer.`);
        });
        return { ok: errors.length === 0, errors };
    }, [quiz]);

    const onSave = async () => {
        if (!validation.ok) {
            toast.error(validation.errors[0]);
            return;
        }
        try {
            setSaving(true);
            const payload = {
                questions: quiz.questions.map((q) => ({
                    ...q,
                    prompt: (q.prompt || "").trim(),
                    options: q.options.map((o) => (o || "").trim()),
                    rationale: (q.rationale || "").trim(),
                })),
                pass_score: Math.max(0, Math.min(1, Number(quiz.pass_score) || 0.7)),
                max_attempts: Math.max(1, Math.min(20, Number(quiz.max_attempts) || 3)),
                retake_allowed: !!quiz.retake_allowed,
            };
            await api.put(`/course-builder/lessons/${lesson.id}/quiz`, payload);
            toast.success("Quiz saved");
            setDirty(false);
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save quiz.");
        } finally {
            setSaving(false);
        }
    };

    if (loading || !quiz) return <div className="p-8 text-sm text-muted-foreground">Loading quiz…</div>;

    return (
        <div className="space-y-5" data-testid="quiz-builder">
            {/* Global settings */}
            <div className="bg-secondary/40 border border-border rounded-xl p-4">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-3">Quiz settings</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <label className="block">
                        <span className="text-[11px] font-semibold text-muted-foreground">Pass score (%)</span>
                        <input
                            type="number"
                            min="0" max="100" step="5"
                            disabled={readOnly}
                            value={Math.round((quiz.pass_score || 0) * 100)}
                            onChange={(e) => set({ pass_score: Math.max(0, Math.min(100, Number(e.target.value))) / 100 })}
                            className="w-full h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm"
                            data-testid="quiz-pass-score"
                        />
                    </label>
                    <label className="block">
                        <span className="text-[11px] font-semibold text-muted-foreground">Max attempts</span>
                        <input
                            type="number"
                            min="1" max="20"
                            disabled={readOnly}
                            value={quiz.max_attempts}
                            onChange={(e) => set({ max_attempts: Number(e.target.value) })}
                            className="w-full h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm"
                            data-testid="quiz-max-attempts"
                        />
                    </label>
                    <label className="flex items-center gap-2 mt-6 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            disabled={readOnly}
                            checked={!!quiz.retake_allowed}
                            onChange={(e) => set({ retake_allowed: e.target.checked })}
                            className="h-4 w-4"
                            data-testid="quiz-retake-toggle"
                        />
                        Allow retakes
                    </label>
                </div>
            </div>

            {/* Questions */}
            <div className="space-y-4" data-testid="quiz-question-list">
                {quiz.questions.map((q, idx) => (
                    <QuestionCard
                        key={idx}
                        idx={idx}
                        q={q}
                        readOnly={readOnly}
                        onChange={(patch) => setQuestion(idx, patch)}
                        onRemove={() => removeQuestion(idx)}
                        onAddOption={() => addOption(idx)}
                        onRemoveOption={(optIdx) => removeOption(idx, optIdx)}
                    />
                ))}
            </div>

            {/* Add question buttons */}
            {!readOnly && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="quiz-add-question">
                    <span className="text-xs font-semibold text-muted-foreground">Add question:</span>
                    {Object.entries(TYPE_META).map(([key, meta]) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => addQuestion(key)}
                            className="inline-flex items-center gap-1 px-3 h-8 rounded-md border border-border bg-secondary text-xs font-semibold hover:bg-primary/10 hover:border-primary/40 hover:text-primary transition-colors"
                            data-testid={`quiz-add-${key}`}
                        >
                            <meta.Icon size={12} weight="duotone" />
                            {meta.label}
                        </button>
                    ))}
                </div>
            )}

            {/* Validation */}
            {!validation.ok && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 flex items-start gap-2">
                    <Warning size={14} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                    <div>
                        <div className="font-semibold mb-0.5">Fix these before saving:</div>
                        <ul className="list-disc pl-4 space-y-0.5">
                            {validation.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                    </div>
                </div>
            )}

            {/* Save */}
            {!readOnly && (
                <div className="flex items-center justify-end pt-2">
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={saving || !dirty || !validation.ok}
                        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-bold disabled:opacity-60 hover:bg-primary/90 transition-colors"
                        data-testid="quiz-save-btn"
                    >
                        <FloppyDisk size={12} weight="fill" />
                        {saving ? "Saving…" : dirty ? "Save quiz" : "Saved"}
                    </button>
                </div>
            )}
        </div>
    );
}


function QuestionCard({ idx, q, readOnly, onChange, onRemove, onAddOption, onRemoveOption }) {
    const meta = TYPE_META[q.type] || TYPE_META.mcq;
    return (
        <div className="bg-card border border-border rounded-xl p-4" data-testid={`quiz-question-${idx}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md bg-primary/10 text-primary text-[10px] font-bold tabular-nums grid place-items-center">
                        {idx + 1}
                    </div>
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        <meta.Icon size={11} weight="fill" />
                        {meta.label}
                    </span>
                </div>
                {!readOnly && (
                    <button
                        type="button"
                        onClick={onRemove}
                        className="w-7 h-7 grid place-items-center rounded-md border border-border text-muted-foreground hover:text-rose-600 hover:bg-rose-50 transition-colors"
                        data-testid={`quiz-remove-question-${idx}`}
                        title="Remove question"
                    >
                        <Trash size={11} weight="bold" />
                    </button>
                )}
            </div>

            <label className="block mb-3">
                <span className="text-[11px] font-semibold text-muted-foreground">Question prompt</span>
                <div className="mt-1">
                    <RichTextEditor
                        value={q.prompt || ""}
                        onChange={(html) => onChange({ prompt: html })}
                        placeholder={q.type === "scenario" ? "Describe the clinical scenario…" : "Ask a clear, specific question…"}
                        disabled={readOnly}
                        size="sm"
                    />
                </div>
                <input type="hidden" data-testid={`quiz-prompt-${idx}`} value={q.prompt || ""} readOnly />
            </label>

            <div className="space-y-1.5">
                <div className="text-[11px] font-semibold text-muted-foreground">Options — click circle to mark correct</div>
                {q.options.map((opt, oIdx) => {
                    const isCorrect = q.answer_index === oIdx;
                    return (
                        <div key={oIdx} className="flex items-center gap-2" data-testid={`quiz-option-row-${idx}-${oIdx}`}>
                            <button
                                type="button"
                                disabled={readOnly}
                                onClick={() => onChange({ answer_index: oIdx })}
                                className={`w-5 h-5 shrink-0 grid place-items-center rounded-full border-2 transition-colors ${
                                    isCorrect
                                        ? "bg-emerald-500 border-emerald-500 text-white"
                                        : "bg-white border-slate-300 text-transparent hover:border-emerald-400"
                                }`}
                                title={isCorrect ? "Correct answer" : "Mark as correct"}
                                data-testid={`quiz-correct-${idx}-${oIdx}`}
                            >
                                {isCorrect ? <CheckCircle size={11} weight="fill" /> : <Circle size={9} weight="regular" />}
                            </button>
                            <input
                                type="text"
                                disabled={readOnly || q.type === "true_false"}
                                value={opt}
                                onChange={(e) => onChange({ options: q.options.map((o, i) => (i === oIdx ? e.target.value : o)) })}
                                className="flex-1 h-8 rounded-md border border-input bg-background px-2 text-sm"
                                placeholder={`Option ${String.fromCharCode(65 + oIdx)}`}
                                data-testid={`quiz-option-${idx}-${oIdx}`}
                            />
                            {!readOnly && q.type !== "true_false" && q.options.length > 2 && (
                                <button
                                    type="button"
                                    onClick={() => onRemoveOption(oIdx)}
                                    className="w-7 h-7 grid place-items-center rounded-md border border-border text-muted-foreground hover:text-rose-600 hover:bg-rose-50 transition-colors"
                                    title="Remove option"
                                    data-testid={`quiz-remove-option-${idx}-${oIdx}`}
                                >
                                    <Trash size={10} weight="bold" />
                                </button>
                            )}
                        </div>
                    );
                })}
                {!readOnly && q.type !== "true_false" && q.options.length < 6 && (
                    <button
                        type="button"
                        onClick={onAddOption}
                        className="text-[11px] font-semibold text-primary hover:text-primary/80 inline-flex items-center gap-1"
                        data-testid={`quiz-add-option-${idx}`}
                    >
                        <Plus size={10} weight="bold" /> Add option
                    </button>
                )}
            </div>

            <label className="block mt-3">
                <span className="text-[11px] font-semibold text-muted-foreground">
                    Rationale <span className="font-normal">(shown to learner after answering)</span>
                </span>
                <div className="mt-1">
                    <RichTextEditor
                        value={q.rationale || ""}
                        onChange={(html) => onChange({ rationale: html })}
                        placeholder="Explain why the correct answer is correct."
                        disabled={readOnly}
                        size="sm"
                    />
                </div>
                <input type="hidden" data-testid={`quiz-rationale-${idx}`} value={q.rationale || ""} readOnly />
            </label>
        </div>
    );
}
